import React, { useContext } from 'react';

import { ColumnFiltersState } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { UserDataContext } from 'lane-shared/contexts';
import { useFlag } from 'lane-shared/hooks';
import { FeatureFlag } from 'constants-flags';
import {
  Button,
  Table,
  Tooltip,
  NativeFilterTypes,
  FilterType,
} from 'design-system-web';
import {
  bulkTeamManagementChannelsQuery,
  distinctGroupRoleNamesQuery,
} from 'graphql-queries';

import { H3, H4, M } from 'components/typography';

import { useFilteredPermissions } from '../../admin/channel/hooks/useFilteredPermissions';

import {
  ActiveWizard,
  BulkTeamManagementContext,
  BulkTeamManagementProvider,
} from './context/BulkTeamManagementContext';
import {
  PermissionQueryType,
  useBulkTeamManagementChannels,
} from './hooks/useBulkTeamManagementChannels';
import {
  buildNameSearchPaginationVariables,
  NameOptionRow,
  useNameFilterOptions,
} from './hooks/useNameFilterOptions';
import { PermissionsAssignmentWizardModal } from './wizard/PermissionsAssignmentWizardModal';
import type { SubmitOutcome } from './wizard/PermissionsAssignmentWizardEngine';

import styles from './styles.scss';

const MAX_SELECTABLE_CHANNELS = 500;
const PARENT_CHANNEL_OPTION_LIMIT = 100;

type DistinctGroupRoleNamesResponse = {
  distinctGroupRoleNames?: {
    items?: { name: string; groupRoleIds: string[] }[];
  };
};

function extractGroupRoleRows(
  data: DistinctGroupRoleNamesResponse
): NameOptionRow[] {
  const items = data.distinctGroupRoleNames?.items ?? [];

  return items.map(item => ({ name: item.name, ids: item.groupRoleIds }));
}

function buildParentChannelVariables(keyword: string) {
  return {
    pagination: { start: 0, perPage: PARENT_CHANNEL_OPTION_LIMIT },
    search: keyword ? { name: { type: 'like', value: keyword } } : {},
  };
}

type BulkTeamManagementChannelsResponse = {
  bulkModuleChannels?: {
    items?: { _id: string; name: string }[];
  };
};

function extractParentChannelRows(
  data: BulkTeamManagementChannelsResponse
): NameOptionRow[] {
  const items = data.bulkModuleChannels?.items ?? [];

  return items.map(item => ({ name: item.name, ids: [item._id] }));
}

function getStringArrayFilterValue(
  columnFilters: ColumnFiltersState,
  id: string
): string[] {
  const value = columnFilters.find(f => f.id === id)?.value;

  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((v): v is string => typeof v === 'string');
}

function getPermissionQueryTypeFilterValue(
  columnFilters: ColumnFiltersState
): PermissionQueryType {
  const value = columnFilters.find(f => f.id === 'permissionQueryType')?.value;

  return value === 'NOT_IN' ? 'NOT_IN' : 'IN';
}

/**
 * Search/filter/table/actions content for the bulk team permission
 * management landing page. Split out so tests can render it directly
 * against a controlled `BulkTeamManagementContext` value.
 */
export function BulkTeamManagementPageContent() {
  const { t } = useTranslation();
  const { setSelectedChannelIds: setContextSelectedIds, setActiveWizard } =
    useContext(BulkTeamManagementContext);

  const channelsHook = useBulkTeamManagementChannels();
  const groupRoleNameFilter =
    useNameFilterOptions<DistinctGroupRoleNamesResponse>(
      distinctGroupRoleNamesQuery,
      buildNameSearchPaginationVariables,
      extractGroupRoleRows
    );
  const parentChannelNameFilter =
    useNameFilterOptions<BulkTeamManagementChannelsResponse>(
      bulkTeamManagementChannelsQuery,
      buildParentChannelVariables,
      extractParentChannelRows
    );

  const permissionCategories = useFilteredPermissions();
  const permissionOptions = permissionCategories.flatMap(category =>
    category.value.map((detail: { key: string; description: string }) => ({
      label: t(detail.description),
      value: detail.key,
    }))
  );

  const filters: FilterType[] = [
    {
      key: 'groupRoleNames',
      type: NativeFilterTypes.Multiselect,
      label: t(
        'web.admin.portalManagement.bulkTeamManagement.filters.groupRoleName'
      ),
      options: [],
      loadOptions: groupRoleNameFilter.loadOptions,
    },
    {
      key: 'parentChannelNames',
      type: NativeFilterTypes.Multiselect,
      label: t(
        'web.admin.portalManagement.bulkTeamManagement.filters.parentChannelName'
      ),
      options: [],
      loadOptions: parentChannelNameFilter.loadOptions,
    },
    {
      key: 'permissions',
      type: NativeFilterTypes.Multiselect,
      label: t(
        'web.admin.portalManagement.bulkTeamManagement.filters.permissions'
      ),
      options: permissionOptions,
    },
    {
      key: 'permissionQueryType',
      type: NativeFilterTypes.Select,
      label: t(
        'web.admin.portalManagement.bulkTeamManagement.filters.permissionQueryType'
      ),
      options: [
        {
          label: t(
            'web.admin.portalManagement.bulkTeamManagement.filters.permissionQueryType.include'
          ),
          value: 'IN',
        },
        {
          label: t(
            'web.admin.portalManagement.bulkTeamManagement.filters.permissionQueryType.exclude'
          ),
          value: 'NOT_IN',
        },
      ],
    },
  ];

  const handleFilterChange = (columnFilters: ColumnFiltersState) => {
    const groupRoleNames = getStringArrayFilterValue(
      columnFilters,
      'groupRoleNames'
    );
    const parentNames = getStringArrayFilterValue(
      columnFilters,
      'parentChannelNames'
    );
    const permissions = getStringArrayFilterValue(columnFilters, 'permissions');
    const permissionQueryType =
      getPermissionQueryTypeFilterValue(columnFilters);

    channelsHook.handleGroupRoleIdsChange(
      groupRoleNameFilter.resolveIdsForNames(groupRoleNames)
    );
    channelsHook.handleParentChannelIdsChange(
      parentChannelNameFilter.resolveIdsForNames(parentNames)
    );
    channelsHook.handlePermissionsChange(permissions);
    channelsHook.handlePermissionQueryTypeChange(permissionQueryType);
  };

  const selectedCount = channelsHook.selectedChannelIds.length;
  const canAssign =
    selectedCount > 0 && selectedCount <= MAX_SELECTABLE_CHANNELS;

  let disabledReason: string | undefined;

  if (selectedCount === 0) {
    disabledReason = t(
      'web.admin.portalManagement.bulkTeamManagement.actions.assignPermissions.disabled.noSelection'
    );
  } else if (selectedCount > MAX_SELECTABLE_CHANNELS) {
    disabledReason = t(
      'web.admin.portalManagement.bulkTeamManagement.actions.assignPermissions.disabled.overCap'
    );
  }

  const handleAssignPermissionsClick = () => {
    if (!canAssign) return;

    setContextSelectedIds(channelsHook.selectedChannelIds);
    setActiveWizard(ActiveWizard.PermissionsAssignment);
  };

  const handleWizardComplete = (
    result: SubmitOutcome,
    channelCount: number
  ) => {
    const succeededCount = result.succeededIds.length;
    const totalCount = succeededCount + result.failed.length;

    if (result.failed.length === 0) {
      window.Toast.show(
        t('web.admin.portalManagement.bulkTeamManagement.outcome.success', {
          succeededCount,
          channelCount,
        })
      );
    } else {
      window.Toast.show(
        t('web.admin.portalManagement.bulkTeamManagement.outcome.partial', {
          succeededCount,
          totalCount,
          failedCount: result.failed.length,
        })
      );
    }

    channelsHook.refetch();
  };

  const assignPermissionsButton = (
    <Button
      disabled={!canAssign}
      onClick={handleAssignPermissionsClick}
      testId="bulkTeamManagementAssignPermissionsButton"
    >
      {t(
        'web.admin.portalManagement.bulkTeamManagement.actions.assignPermissions'
      )}
    </Button>
  );

  return (
    <div data-test="bulkTeamManagementPage" className={styles.container}>
      <div className={styles.header}>
        <H3 mb={2}>
          {t('web.admin.portalManagement.bulkTeamManagement.page.title')}
        </H3>
        <M>
          {t('web.admin.portalManagement.bulkTeamManagement.page.description')}
        </M>
      </div>

      <div className={styles.table}>
        <Table
          isSelectable
          columns={[
            {
              key: 'name',
              header: t(
                'web.admin.portalManagement.bulkTeamManagement.table.column.name'
              ),
              disableSorting: true,
            },
            {
              key: 'type',
              header: t(
                'web.admin.portalManagement.bulkTeamManagement.table.column.type'
              ),
              disableSorting: true,
            },
            {
              key: 'parentName',
              header: t(
                'web.admin.portalManagement.bulkTeamManagement.table.column.parent'
              ),
              disableSorting: true,
            },
          ]}
          data={channelsHook.channels.map(
            (ch: {
              _id: string;
              name: string;
              type: string;
              parent?: { name: string } | null;
            }) => ({
              _id: ch._id,
              name: ch.name,
              type: ch.type,
              parentName: ch.parent?.name ?? '—',
            })
          )}
          isLoading={channelsHook.loading}
          totalRows={channelsHook.total}
          hasKeywordFilter
          filters={filters}
          pagination="server"
          onKeywordFilterChange={channelsHook.handleKeywordChange}
          onFilterChange={handleFilterChange}
          onPageChange={channelsHook.handlePageChange}
          page={channelsHook.page}
          pageSize={channelsHook.pageSize}
          onPageSizeChange={channelsHook.handlePageSizeChange}
          rowSelectionExternal
          rowSelection={channelsHook.selectedChannelIds}
          getRowId={(row: { _id: string }) => row._id}
          onSelectionChange={channelsHook.setSelectedChannelIds}
          tableKey="portalManagement.bulkTeamManagementTable"
        />
      </div>

      <H4>
        {t('web.admin.portalManagement.bulkTeamManagement.actions.title')}
      </H4>
      <div className={styles.actions}>
        {disabledReason ? (
          <Tooltip TooltipComponent={disabledReason} placement="top">
            {assignPermissionsButton}
          </Tooltip>
        ) : (
          assignPermissionsButton
        )}
        <span className={styles.selectionCount}>
          {t(
            'web.admin.portalManagement.bulkTeamManagement.actions.selectedCount',
            {
              count: selectedCount,
              total: channelsHook.total,
            }
          )}
        </span>
      </div>

      <PermissionsAssignmentWizardModal onComplete={handleWizardComplete} />
    </div>
  );
}

export function BulkTeamManagementPage() {
  const { user } = useContext(UserDataContext);
  const isEnabled = useFlag(FeatureFlag.BulkChannelTeamPermissions, false);

  if (!user?.isSuperUser || !isEnabled) {
    return null;
  }

  return (
    <BulkTeamManagementProvider>
      <BulkTeamManagementPageContent />
    </BulkTeamManagementProvider>
  );
}
